<template>
  <div class="signup-container">
    <v-container fluid fill-height>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="10" md="8" lg="6">
          <v-card class="elevation-12">
            <v-toolbar color="primary" dark flat>
              <v-toolbar-title>{{ t('auth.signup') }}</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-form ref="form" @submit.prevent="handleSubmit">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.username"
                      :label="t('auth.username')"
                      prepend-icon="mdi-account"
                      :rules="[rules.required, rules.usernameLength]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.email"
                      :label="t('auth.email')"
                      prepend-icon="mdi-email"
                      type="email"
                      :rules="[rules.required, rules.email]"
                      required
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.firstName"
                      :label="t('user.firstName')"
                      prepend-icon="mdi-account"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.lastName"
                      :label="t('user.lastName')"
                      prepend-icon="mdi-account"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.phone"
                      :label="t('user.phone')"
                      prepend-icon="mdi-phone"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.dob"
                      :label="t('user.dateOfBirth')"
                      prepend-icon="mdi-calendar"
                      type="date"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="formData.gender"
                      :label="t('user.gender')"
                      :items="genderOptions"
                      prepend-icon="mdi-gender-male-female"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.address"
                      :label="t('user.address')"
                      prepend-icon="mdi-home"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.password"
                      :label="t('auth.password')"
                      prepend-icon="mdi-lock"
                      :type="showPassword ? 'text' : 'password'"
                      :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      :rules="[rules.required, rules.passwordLength]"
                      required
                      @click:append="showPassword = !showPassword"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="confirmPassword"
                      :label="t('auth.confirmPassword')"
                      prepend-icon="mdi-lock"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      :rules="[rules.required, rules.passwordMatch]"
                      required
                      @click:append="showConfirmPassword = !showConfirmPassword"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="goToLogin">{{ t('auth.alreadyHaveAccount') }}</v-btn>
              <v-spacer />
              <v-btn
                :loading="loading"
                color="primary"
                :disabled="!isFormValid"
                @click="handleSubmit"
              >
                {{ t('auth.signup') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { authService, type SignupRequest } from '@/services/authService';
import { useNotification } from '@/composables/useNotification';

const router = useRouter();
const { t } = useI18n();
const { showSuccess, showError } = useNotification();

const form = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const loading = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const confirmPassword = ref('');

const formData = ref<SignupRequest>({
  username: '',
  email: '',
  password: '',
  phone: '',
  firstName: '',
  lastName: '',
  dob: '',
  gender: 'MALE',
  address: '',
  role: ['user'],
});

const genderOptions = [
  { title: t('user.male'), value: 'MALE' },
  { title: t('user.female'), value: 'FEMALE' },
  { title: t('user.other'), value: 'OTHER' },
];

const rules = {
  required: (v: string) => !!v || t('validation.required'),
  email: (v: string) => /.+@.+\..+/.test(v) || t('auth.invalidEmail'),
  usernameLength: (v: string) => (v && v.length >= 3 && v.length <= 50) || t('auth.usernameLength'),
  passwordLength: (v: string) => (v && v.length >= 6 && v.length <= 40) || t('auth.passwordLength'),
  passwordMatch: (v: string) => v === formData.value.password || t('auth.passwordsDoNotMatch'),
};

const isFormValid = computed(() => {
  return (
    formData.value.username &&
    formData.value.email &&
    formData.value.password &&
    formData.value.phone &&
    formData.value.firstName &&
    formData.value.lastName &&
    formData.value.dob &&
    formData.value.gender &&
    confirmPassword.value === formData.value.password
  );
});

const handleSubmit = async () => {
  if (form.value) {
    const { valid } = await form.value.validate();
    if (valid) {
      loading.value = true;
      try {
        const response = await authService.signup(formData.value);
        showSuccess(response.data?.message || t('auth.signupSuccess'));
        // Redirect to login page after successful signup
        setTimeout(() => {
          router.push('/login');
        }, 1500);
      } catch (error) {
        console.error('Signup failed:', error);
        showError(t('auth.signupError'));
      } finally {
        loading.value = false;
      }
    }
  }
};

const goToLogin = () => {
  router.push('/login');
};
</script>

<style scoped>
.signup-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}
</style>
